import { Card, CardContent, CardMedia, Stack, Typography } from '@mui/material';
import React from 'react';
import { defineMessages, type MessageDescriptor, useIntl } from 'react-intl';

import AlertSettingToggle from '@images/info-drawer/alerts/AlertSettingToggle.png';
import VerifiedDestination from '@images/info-drawer/alerts/VerifiedDestination.png';

import { AlertRecipientType } from 'generated/types';

const messages = defineMessages({
  'user.title': {
    id: 'drawer.alertRecipient.user.title',
    defaultMessage:
      'Setup alerts to be sent to your personal email address and/or mobile number',
  },
  'user.subtitle': {
    id: 'drawer.alertRecipient.user.subtitle',
    defaultMessage: '',
  },
  'account.title': {
    id: 'drawer.alertRecipient.account.title',
    defaultMessage:
      'Create a global organization recipient that will receive all email/sms alerts for all patients within your organization.',
  },
  'account.subtitle': {
    id: 'drawer.alertRecipient.account.subtitle',
    defaultMessage:
      'The global organization recipient will have alert notifications sent to the email address and/or mobile phone number below, once verified and enabled.',
  },
  'enable.title': {
    id: 'drawer.alertRecipient.enable.title',
    defaultMessage: 'Enable alert recipient settings',
  },
  'enable.subtitle': {
    id: 'drawer.alertRecipient.enable.subtitle',
    defaultMessage:
      'Slide the toggle to the right to enable email/sms alert delivery.',
  },
  'verify.title': {
    id: 'drawer.alertRecipient.verify.title',
    defaultMessage: 'Verify your email/phone number',
  },
  'verify.subtitle': {
    id: 'drawer.alertRecipient.verify.subtitle',
    defaultMessage:
      'Enter your email or mobile number. A six-digit code will be sent to the provided email/phone. Enter the code to complete the verification process. Once successful, a green verified tag should appear.',
  },
});

interface AlertRecipientDrawerContentProps {
  type: AlertRecipientType;
}

export default function AlertRecipientDrawerContent({
  type,
}: AlertRecipientDrawerContentProps) {
  const { formatMessage } = useIntl();

  const steps = [
    {
      title: messages['verify.title'],
      subtitle: messages['verify.subtitle'],
      image: VerifiedDestination,
    },
    {
      title: messages['enable.title'],
      subtitle: messages['enable.subtitle'],
      image: AlertSettingToggle,
    },
  ];

  const textRecord: Record<
    AlertRecipientType,
    {
      title: MessageDescriptor;
      showSubtitle: boolean;
      subtitle: MessageDescriptor;
    }
  > = {
    [AlertRecipientType.account]: {
      title: messages['account.title'],
      showSubtitle: true,
      subtitle: messages['account.subtitle'],
    },
    [AlertRecipientType.user]: {
      title: messages['user.title'],
      showSubtitle: false,
      subtitle: messages['user.subtitle'],
    },
  };

  const descriptor = textRecord[type];

  return (
    <Stack>
      <Typography variant="body1">
        {formatMessage(textRecord[type].title)}
      </Typography>
      {textRecord[type].showSubtitle && (
        <Typography variant="body2">
          {formatMessage(descriptor.subtitle)}
        </Typography>
      )}

      {steps.map(({ image, title, subtitle }) => (
        <Card key={title.id}>
          <CardContent>
            <Typography color="purple.main" variant="body1">
              {formatMessage(title)}
            </Typography>
            <Typography color="text.secondary" variant="body2">
              {formatMessage(subtitle)}
            </Typography>
            <CardMedia
              component="img"
              image={image}
              alt="Instructions"
              sx={{ boxShadow: (theme) => theme.shadows[1] }}
            />
          </CardContent>
        </Card>
      ))}
    </Stack>
  );
}
